import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {AuthService} from '@auth0/auth0-angular';
import {catchError, Observable, of, switchMap, take, timeout} from 'rxjs';
import {environment} from '../../environments/environment';

/** Public calls the backend serves without sign-in - see the backend SecurityConfiguration. */
const PUBLIC_POSTS = ['/products/ids', '/products/search', '/orders/apply-coupon', '/notifications/message', '/customers/subscribe', '/reviews', '/reminders'];
const PROTECTED_GETS = ['/customers', '/orders', '/products/discounts', '/admin', '/notifications'];

/**
 * Sends the Auth0 ID token with calls that need a signed-in user (admin panel, own profile,
 * spending points). Public shop calls go out untouched, so browsing and prerendering never
 * wait for Auth0.
 */
@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {

    constructor(private readonly auth: AuthService) {
    }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!this.needsToken(request)) {
            return next.handle(request);
        }

        return this.auth.isAuthenticated$.pipe(
            take(1),
            timeout({first: 5000, with: () => of(false)}),
            switchMap(isAuthenticated => isAuthenticated ? this.freshIdToken() : of(null)),
            switchMap(token => next.handle(token
                ? request.clone({setHeaders: {Authorization: `Bearer ${token}`}})
                : request))
        );
    }

    /** Asking for an access token makes the SDK renew the session - and the ID token with it - when it has expired. */
    private freshIdToken(): Observable<string | null> {
        return this.auth.getAccessTokenSilently().pipe(
            catchError(() => of(null)),
            switchMap(() => this.auth.idTokenClaims$.pipe(take(1))),
            switchMap(claims => {
                const valid = !!claims?.__raw && !!claims.exp && claims.exp * 1000 > Date.now() + 30000;
                return of(valid ? claims!.__raw : null);
            }),
            catchError(() => of(null))
        );
    }

    private needsToken(request: HttpRequest<unknown>): boolean {
        if (!request.url.startsWith(environment.domain.backend)) {
            return false;
        }
        const path = request.url.substring(environment.domain.backend.length).split('?')[0];

        if (request.method === 'GET') {
            return PROTECTED_GETS.some(prefix => path === prefix || path.startsWith(prefix + '/'));
        }
        return !PUBLIC_POSTS.includes(path);
    }
}
